/**
 * Cross-service contract for the "proactive dispatch" (disparo ativo) flow.
 *
 * `apps/services/conversation` owns the conversation identity: it mints the
 * `conversationId`, delivers the outbound message to the customer, then
 * publishes a `ProactiveDispatchEvent` on its own SQS queue. `apps/services/
 * agents` consumes it WITHOUT running the graph (no LLM) — it builds the
 * kanban state (card + a task that is born `blocked` waiting on the client)
 * and seeds the checkpointer memory with the proactive message, so the AI has
 * context when the customer replies through the normal inbound flow.
 *
 * Deliberately a SEPARATE schema from `AgentsEventSchema` (own queue =
 * discriminator): different semantics (a pre-delivered outbound + a card
 * brief, no inbound `messages`), and keeping it apart avoids polluting the
 * `.strict()` surface of `AgentsEvent`. Field names mirror `AgentsEvent`
 * where they overlap (`messageId`, `correlationId`, `conversationId`,
 * `orgId`, `source`, `domainHint`, `metadata`) so both contracts read the
 * same. Top-level `.strict()`, nested objects inherit Zod's default strip —
 * same precedent as `AgentsEventSchema`.
 */

import { z } from 'zod';

import { CardIdSchema } from '../card/card_id';
import { AgentsEventSourceSchema } from './agents';
import { ConversationIdSchema, MessageIdSchema } from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';
import { PrioritySchema } from '../card/priority';

/**
 * Card brief carried in the event — the dispatch already knows the purpose of
 * the outreach, so there is no summarizer on the agents side (the graph never
 * runs). Limits match the card's own `subject`/`summary` constraints.
 */
export const ProactiveDispatchCardSchema = z.object({
  subject: z.string().min(1).max(200),
  summary: z.string().min(1).max(2000),
  priority: PrioritySchema,
});

export type ProactiveDispatchCard = z.infer<typeof ProactiveDispatchCardSchema>;

export const ProactiveDispatchEventSchema = z
  .object({
    messageId: MessageIdSchema,
    correlationId: z.uuid(),
    conversationId: ConversationIdSchema,
    orgId: EnvelopeOrgIdSchema,
    source: AgentsEventSourceSchema,
    // The proactive message the conversation service has ALREADY delivered to
    // the customer. Seeds the checkpointer memory and grounds the card.
    proactiveMessage: z.object({
      text: z.string().min(1),
      at: z.iso.datetime(),
    }),
    card: ProactiveDispatchCardSchema,
    // Optional pre-minted card id. When present it wins; when absent (no
    // producer sends it today) the agents consumer mints the card itself via
    // its `ensureCard` (agents card ownership, decision 2026-07-03 — both v1
    // and agents-v2) before opening the born-blocked task.
    cardId: CardIdSchema.optional(),
    // Customer/company snapshot for the card — same convention as
    // `AgentsEvent.metadata`.
    metadata: z.record(z.string(), z.unknown()).optional(),
    // Optional pre-routing to a domain; absent → card-level AI (agents-service).
    domainHint: z
      .enum(['hotel', 'flight', 'mobility', 'rental', 'flight_schedule_change'])
      .nullish(),
  })
  .strict();

export type ProactiveDispatchEvent = z.infer<typeof ProactiveDispatchEventSchema>;
